"use client";

import { usePathname } from "next/navigation";
import { CalendarPlus, Download } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { isEligibleForCalendar, buildGoogleCalendarUrl, downloadICS } from "@/lib/calendar";
import type { CiudadVivaEvent } from "@/lib/events";
import { useMessages } from "@/lib/i18n/client";
import messages from "./AddToCalendarButton.i18n";
import { track } from "@/lib/tracking/track";

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL ?? "https://ciudad-viva.cl";

interface Props {
  event: CiudadVivaEvent;
}

export function AddToCalendarButton({ event }: Props) {
  const pathname = usePathname();
  const t = useMessages(messages);

  if (!isEligibleForCalendar(event)) return null;

  const pageUrl = `${SITE_URL}${pathname}`;

  return (
    <Popover>
      <PopoverTrigger className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
        <CalendarPlus className="h-4 w-4" />
        <span className="text-sm">{t.addToCalendar}</span>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <a
          href={buildGoogleCalendarUrl(event, pageUrl)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("add_to_calendar", { slug: event.slug, calendar: "google" })}
          className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm hover:bg-muted"
        >
          <CalendarPlus className="h-4 w-4 text-muted-foreground" />
          Google Calendar
        </a>
        <button
          onClick={() => {
            downloadICS(event, pageUrl);
            track("add_to_calendar", { slug: event.slug, calendar: "ics" });
          }}
          className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-muted"
        >
          <Download className="h-4 w-4 text-muted-foreground" />
          {t.downloadIcs}
        </button>
      </PopoverContent>
    </Popover>
  );
}
